import { ChangeDetectionStrategy, Component, computed, effect, inject, input, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';

import { FieldInterventionEntityBloc } from '@connect-services/client/blocs/field-intervention-entity.bloc';
import { FieldInterventionFormGroupedControlsComponent } from '@connect-services/client/components/field-intervention-form-grouped-controls/field-intervention-form-grouped-controls.component';
import {
    FieldInterventionControlResultDetails,
    FieldInterventionService,
} from '@connect-services/client/services/field-intervention.service';
import {
    FieldInterventionControlsCategory,
    FieldInterventionControlsCategoryWithKey,
    FieldInterventionControlsGroup,
    JsonFormService,
} from '@connect-services/client/services/json-form.service';
import { TabComponent, TabWithState } from '@connect-services/src/lib/components/tab/tab.component';
import {
    FieldInterventionControlResult,
    FieldInterventionEntity,
    FieldInterventionForm,
    FieldInterventionFormsService,
    JsonForm,
    Person,
} from '@connect-services/src/sdk/api';
import { RessourcesModule } from '@ressources/client/ressources.module';
import { TabContentDirective } from '@shared/client/directives/tab-content.directive';
import { SnackBarService } from '@shared/client/services/snack-bar.service';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [FieldInterventionFormGroupedControlsComponent, RessourcesModule, TabComponent, TabContentDirective],
    selector: 'app-field-intervention-form-category',
    templateUrl: './field-intervention-form-category.component.html',
})
export class FieldInterventionFormCategoryComponent {
    public readonly currentForm = input.required<FieldInterventionForm | null>();
    public readonly isResultRequired = input<boolean>(true);

    // eslint-disable-next-line @typescript-eslint/member-ordering
    public readonly categoriesNames = computed<string[]>(
        () =>
            this.controlsGroupedByCategory().map(
                (category: FieldInterventionControlsCategoryWithKey) =>
                    category.category[0].toUpperCase() + category.category.slice(1),
            ) ?? [],
    );
    public readonly controlsGroupedByCategory = signal<FieldInterventionControlsCategoryWithKey[]>([]);
    public readonly fieldInterventionControlResults = signal<FieldInterventionControlResultDetails[]>([]);

    protected readonly isLoading = signal<boolean>(true);
    protected readonly tabs = computed<TabWithState[]>(() =>
        this.categoriesNames().map((name: string) => ({ active: true, name: name })),
    );

    private readonly fieldInterventionEntityBloc = inject(FieldInterventionEntityBloc);
    private readonly fieldInterventionFormService = inject(FieldInterventionFormsService);
    private readonly fieldInterventionService = inject(FieldInterventionService);
    private readonly jsonFormService = inject(JsonFormService);
    private readonly snackbarService = inject(SnackBarService);

    public constructor() {
        effect(() => {
            const currentForm: FieldInterventionForm | null = this.currentForm();
            const isResultRequired: boolean = this.isResultRequired();
            const fieldInterventionEntity: FieldInterventionEntity | null = this.fieldInterventionEntityBloc.value();

            void this.loadForm(currentForm, isResultRequired, fieldInterventionEntity);
        });
    }

    protected updateControlResult(updatedControlResult: FieldInterventionControlResult): void {
        this.fieldInterventionControlResults.update(
            (results: FieldInterventionControlResultDetails[]): FieldInterventionControlResultDetails[] => {
                return results.map(
                    (result: FieldInterventionControlResultDetails): FieldInterventionControlResultDetails => {
                        if (result.id !== updatedControlResult.id) {
                            return result;
                        }

                        return {
                            ...result,
                            comment: updatedControlResult.comment ?? result.comment,
                            statusUpdatedAt: updatedControlResult.statusUpdatedAt ?? result.statusUpdatedAt,
                            validation: updatedControlResult.validation ?? result.validation,
                        };
                    },
                );
            },
        );
    }

    private async fetchJsonForm(currentFormId: string): Promise<JsonForm | null> {
        const jsonForm: JsonForm = await firstValueFrom(
            this.fieldInterventionFormService.fieldInterventionFormControllerJsonSchemaForm({
                id: currentFormId,
            }),
        );

        return jsonForm ?? null;
    }

    private getControlsGroupedByCategoryWithKeys(jsonForm: JsonForm): FieldInterventionControlsCategoryWithKey[] {
        const groupControlsByCategory: FieldInterventionControlsCategory[] =
            this.jsonFormService.fieldInterventionGroupControlsByCategory(jsonForm);

        return groupControlsByCategory.map((category: FieldInterventionControlsCategory, index: number) => ({
            ...category,
            groupedControls: category.groupedControls.map((group: FieldInterventionControlsGroup) => ({
                ...group,
                key: `${category.category}_${group.title}`,
            })),
            key: `${category.category}_${index}`,
        }));
    }

    private async loadControlResults(
        fieldInterventionEntityId: string,
        currentFormId: string,
        jsonForm: JsonForm,
        people: Person[],
    ): Promise<void> {
        const [controlResults, controlResultsMetadata] = await Promise.all([
            this.fieldInterventionService.fetchFieldInterventionControlResultsByEntityAndForm(
                fieldInterventionEntityId,
                currentFormId,
            ),
            this.fieldInterventionService.fetchFieldInterventionControlResultMetadata(
                fieldInterventionEntityId,
                currentFormId,
            ),
        ]);

        this.fieldInterventionControlResults.set(
            this.fieldInterventionService.buildFieldInterventionControlResultDetails(
                controlResults,
                controlResultsMetadata,
                jsonForm,
                people,
            ),
        );
    }

    private async loadForm(
        currentForm: FieldInterventionForm | null,
        isResultRequired: boolean,
        fieldInterventionEntity: FieldInterventionEntity | null,
    ): Promise<void> {
        if (!currentForm) {
            this.isLoading.set(false);

            return;
        }

        this.isLoading.set(true);
        this.fieldInterventionControlResults.set([]);
        try {
            const jsonForm: JsonForm | null = await this.fetchJsonForm(currentForm.id);

            if (!jsonForm) {
                return;
            }

            this.controlsGroupedByCategory.set(this.getControlsGroupedByCategoryWithKeys(jsonForm));

            if (!isResultRequired || !fieldInterventionEntity) {
                return;
            }

            await this.loadControlResults(
                fieldInterventionEntity.id,
                currentForm.id,
                jsonForm,
                fieldInterventionEntity.people ?? [],
            );
        } catch {
            this.snackbarService.openErrorSnackBar('errorGET');
        } finally {
            this.isLoading.set(false);
        }
    }
}
